import { Controller } from "@hotwired/stimulus";
import { rrFetch } from "../utils/rr_fetch";
import { buildRewardsLoadQuery, isRewardsNearBottom, resolveRewardsLimit } from "./rewardsTabsHelpers.mjs";

// Manages the Active / Pending / Payouts tab bar on the rewards summary page.
// Each tab button carries a `data-rewards-tabs-panel-param` value matching the
// id suffix of its corresponding panel (`rewards-panel-{value}`).
export default class RewardsTabsController extends Controller {
  static targets = ["tab", "panel", "scrollContainer"];
  static values = {
    limit: Number,
  };

  connect() {
    this.loadingPanels = {};
    this.hasScrolled = false;
    this.handleScroll = this.#onScroll.bind(this);
    this.currentPanel = this.tabTargets[0]?.dataset.rewardsTabsPanelParam || "active";
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.addEventListener("scroll", this.handleScroll, { passive: true });
    }
    this.#activateTab(0);
    this.observeLastRow();
  }

  disconnect() {
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.removeEventListener("scroll", this.handleScroll);
    }
    this.cleanupObserver();
  }

  show({ params: { panel } }) {
    const idx = this.tabTargets.findIndex(
      (t) => t.dataset.rewardsTabsPanelParam === panel
    );
    if (idx < 0 || panel === this.currentPanel) {
      return;
    }

    this.currentPanel = panel;
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.scrollTop = 0;
    }
    this.#activateTab(idx);
    this.observeLastRow();
  }

  observeLastRow() {
    this.cleanupObserver();

    const activePanel = this.activePanelElement;
    if (!activePanel) {
      return;
    }

    const rows = activePanel.querySelectorAll('[data-rewards-tabs-target="row"]');
    const lastRow = rows[rows.length - 1];
    if (!lastRow) {
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.hasScrolled) {
          this.loadMore();
        }
      });
    }, {
      root: this.hasScrollContainerTarget ? this.scrollContainerTarget : null,
      threshold: 0,
    });

    this.observer.observe(lastRow);
  }

  cleanupObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  async loadMore() {
    const panel = this.currentPanel;
    if (this.loadingPanels[panel]) {
      return;
    }

    const activePanel = this.activePanelElement;
    if (!activePanel) {
      return;
    }

    const rows = activePanel.querySelectorAll('[data-rewards-tabs-target="row"]');
    const lastRow = rows[rows.length - 1];
    const cursor = lastRow?.dataset.cursor;
    const cursorId = lastRow?.dataset.cursorId;
    if (!cursor) {
      this.cleanupObserver();
      return;
    }

    const query = buildRewardsLoadQuery({ panel, cursor, limit: this.limit, cursorId });

    this.loadingPanels[panel] = true;

    try {
      const response = await rrFetch(`/rewards/list/load?${query.toString()}`, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      });

      if (response.status === 204) {
        this.cleanupObserver();
        return;
      }

      if (!response.ok) {
        return;
      }

      const stream = await response.text();
      // Tear down the observer BEFORE rendering so the old observer cannot
      // fire again on the updated DOM and issue a duplicate request.
      this.cleanupObserver();
      Turbo.renderStreamMessage(stream);
      requestAnimationFrame(() => this.observeLastRow());
    } finally {
      this.loadingPanels[panel] = false;
    }
  }

  get limit() {
    return resolveRewardsLimit(this.hasLimitValue, this.limitValue);
  }

  get activePanelElement() {
    return this.panelTargets.find((panel) => panel.dataset.panel === this.currentPanel);
  }

  #onScroll() {
    this.hasScrolled = true;
    if (this.isNearBottom()) {
      this.loadMore();
    }
  }

  isNearBottom() {
    if (!this.hasScrollContainerTarget) {
      return false;
    }

    const el = this.scrollContainerTarget;
    return isRewardsNearBottom(el.scrollHeight, el.scrollTop, el.clientHeight);
  }

  #activateTab(activeIdx) {
    this.tabTargets.forEach((tab, i) => {
      const isActive = i === activeIdx;
      tab.classList.toggle("wdark:bg-wdark-primary-color", isActive);
      tab.classList.toggle("wlight:bg-wlight-primary-color", isActive);
      tab.classList.toggle("dark:bg-dark-primary-color", isActive);
      tab.classList.toggle("wdark:text-wdark-background-color", isActive);
      tab.classList.toggle("wlight:text-wlight-background-color", isActive);
      tab.classList.toggle("dark:text-dark-background-color", isActive);
      tab.classList.toggle("wdark:text-wdark-primary-color", !isActive);
      tab.classList.toggle("wlight:text-wlight-primary-color", !isActive);
      tab.classList.toggle("dark:text-dark-primary-color", !isActive);
      tab.classList.toggle("wdark:bg-wdark-secondary-color", !isActive);
      tab.classList.toggle("wlight:bg-wlight-secondary-color", !isActive);
      tab.classList.toggle("dark:bg-dark-secondary-color", !isActive);
    });

    this.panelTargets.forEach((panel, i) => {
      const isActive = i === activeIdx;
      panel.classList.toggle("hidden", !isActive);
      panel.classList.toggle("flex", isActive);
    });
  }
}
